import { BigBodyGroup, UserWorkoutSession } from 'fitbeat.gym-core';
import { IUserWorkoutSessionSummary } from 'fitbeat.models';
type SortOrder = 'asc' | 'desc';

const sortByStartDateAndFallbackToRemoved = (
    a: IUserWorkoutSessionSummary,
    b: IUserWorkoutSessionSummary,
    order: SortOrder = 'desc',
): number => {
    const aDate = new Date(a.startTimeOfFirstExercise ?? a.startTime ?? 0);
    aDate.setSeconds(0, 0);
    const bDate = new Date(b.startTimeOfFirstExercise ?? b.startTime ?? 0);
    bDate.setSeconds(0, 0);

    const diff = order === 'desc'
        ? bDate.getTime() - aDate.getTime()
        : aDate.getTime() - bDate.getTime();
    if (diff === 0) {
        const aHasRemovedAt = !!a.removedAt;
        const bHasRemovedAt = !!b.removedAt;

        if (aHasRemovedAt && !bHasRemovedAt) {
            return 1;
        } else if (!aHasRemovedAt && bHasRemovedAt) {
            return -1;
        }
    }

    return diff;
};

export const sortByDate = (
    workoutHistory: IUserWorkoutSessionSummary[],
    order: SortOrder,
): IUserWorkoutSessionSummary[] => {
    return [...workoutHistory].sort((a, b) => {
        return sortByStartDateAndFallbackToRemoved(a, b, order);
    });
};

export const sortByDuration = (
    workoutHistory: IUserWorkoutSessionSummary[],
    order: SortOrder,
): IUserWorkoutSessionSummary[] => {
    return [...workoutHistory].sort((a, b) => {
        const aDuration = UserWorkoutSession.calculateWorkoutDurationInMinutes(a.startTimeOfFirstExercise, a.endTime);
        const bDuration = UserWorkoutSession.calculateWorkoutDurationInMinutes(b.startTimeOfFirstExercise, b.endTime);

        let diff = 0;
        if (order === 'desc') {
            diff = bDuration - aDuration;
        } else {
            diff = aDuration - bDuration;
        }

        if (diff === 0) {
            return sortByStartDateAndFallbackToRemoved(a, b);
        }
        return diff;
    });
};

export const sortByActivityType = (
    workoutHistory: IUserWorkoutSessionSummary[],
    order: SortOrder,
): IUserWorkoutSessionSummary[] => {
    return [...workoutHistory].sort((a, b) => {
        const aActivityType = a.settings?.activityType || '';
        const bActivityType = b.settings?.activityType || '';

        let diff = 0;
        if (order === 'desc') {
            diff = bActivityType.localeCompare(aActivityType);
        } else {
            diff = aActivityType.localeCompare(bActivityType);
        }

        if (diff === 0) {
            return sortByStartDateAndFallbackToRemoved(a, b);
        }
        return diff;
    });
};

export const sortByBodyGroups = (
    workoutHistory: IUserWorkoutSessionSummary[],
    order: SortOrder,
): IUserWorkoutSessionSummary[] => {
    return [...workoutHistory].sort((a, b) => {
        const aBodyGroupValue = reduceBodyGroupValue(a.settings?.threeBodyGroups || []);
        const bBodyGroupValue = reduceBodyGroupValue(b.settings?.threeBodyGroups || []);

        let diff = 0;
        if (order === 'desc') {
            diff = bBodyGroupValue - aBodyGroupValue;
        } else {
            diff = aBodyGroupValue - bBodyGroupValue;
        }

        if (diff === 0) {
            return sortByStartDateAndFallbackToRemoved(a, b);
        }
        return diff;
    });
};

export const sortByLoad = (
    workoutHistory: IUserWorkoutSessionSummary[],
    order: SortOrder,
): IUserWorkoutSessionSummary[] => {
    return [...workoutHistory].sort((a, b) => {
        const aLoad = a.performanceMetrics?.load || -1;
        const bLoad = b.performanceMetrics?.load || -1;

        let diff = 0;
        if (order === 'desc') {
            diff = bLoad - aLoad;
        } else {
            diff = aLoad - bLoad;
        }

        if (diff === 0) {
            return sortByStartDateAndFallbackToRemoved(a, b);
        }
        return diff;
    });
};

export const sortByCals = (
    workoutHistory: IUserWorkoutSessionSummary[],
    order: SortOrder,
): IUserWorkoutSessionSummary[] => {
    return [...workoutHistory].sort((a, b) => {
        const aCals = a.performanceMetrics?.calories || -1;
        const bCals = b.performanceMetrics?.calories || -1;

        let diff = 0;
        if (order === 'desc') {
            diff = bCals - aCals;
        } else {
            diff = aCals - bCals;
        }

        if (diff === 0) {
            return sortByStartDateAndFallbackToRemoved(a, b);
        }
        return diff;
    });
};

export const sortByAverageHeartRate = (
    workoutHistory: IUserWorkoutSessionSummary[],
    order: SortOrder,
): IUserWorkoutSessionSummary[] => {
    return [...workoutHistory].sort((a, b) => {
        const aEffort = a.performanceMetrics?.averageEffort || -1;
        const bEffort = b.performanceMetrics?.averageEffort || -1;

        let diff = 0;
        if (order === 'desc') {
            diff = bEffort - aEffort;
        } else {
            diff = aEffort - bEffort;
        }

        if (diff === 0) {
            return sortByStartDateAndFallbackToRemoved(a, b);
        }
        return diff;
    });
};

const bodyGroupValues: Record<string, number> = {
    [BigBodyGroup.Upper]: 6,
    [BigBodyGroup.BackAndArms]: 8,
    [BigBodyGroup.Lower]: 10,
};

const reduceBodyGroupValue = (threeBodyGroups: string[]): number => {
    if (threeBodyGroups.length === 0) {
        return 30;
    }
    return threeBodyGroups.reduce((total, bodygroup) => total + (bodyGroupValues[bodygroup] || 0), 0);
};
